import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Paper } from "@material-ui/core";
import { Spinner } from "react-bootstrap";
import ReactToPrint from "react-to-print";
import ReactApexChart from "react-apexcharts";
import { DownloadTableExcel } from "react-export-table-to-excel";
import { useAlert } from "react-alert";
import { upperCase } from "lodash";
import { baseUrl, config } from "../../../services/config";

// const dummyArray = [
//   [
//     {
//       branch_id: 2,
//       branch_resource: {
//         id: 2,
//         name: "Branch Name",
//         branch_url: "https://site.com",
//         backend_url: "https://portal.site.com",
//       },
//       total_inquiries: 48,
//       total_converted: 18,
//       school_website: 2,
//       facebook: 3,
//       instagram: 9,
//       google_search: 1,
//       whatsapp: 4,
//       referred_parent: 9,
//       walk_in_visit: 10,
//       billboard: 0,
//       newspaper: 10,
//       school_event: 0,
//       other: 0,
//     },
//   ],
//   [
//     {
//       branch_id: 3,
//       branch_resource: {
//         id: 3,
//         name: "Central Park",
//         branch_url: "https://site.com",
//         backend_url: "https://portal.site.com",
//       },
//       total_inquiries: 34,
//       total_converted: 11,
//       school_website: 3,
//       facebook: 1,
//       instagram: 1,
//       google_search: 1,
//       whatsapp: 1,
//       referred_parent: 4,
//       walk_in_visit: 9,
//       billboard: 5,
//       newspaper: 3,
//       school_event: 0,
//       other: 6,
//     },
//   ],
//   [
//     {
//       branch_id: 4,
//       branch_resource: {
//         id: 4,
//         name: "Johar Town",
//         branch_url: "https://site.com",
//         backend_url: "https://portal.site.com",
//       },
//       total_inquiries: 49,
//       total_converted: 19,
//       school_website: 4,
//       facebook: 8,
//       instagram: 4,
//       google_search: 0,
//       whatsapp: 6,
//       referred_parent: 8,
//       walk_in_visit: 7,
//       billboard: 9,
//       newspaper: 1,
//       school_event: 2,
//       other: 0,
//     },
//   ],
//   [
//     {
//       branch_id: 5,
//       branch_resource: {
//         id: 5,
//         name: "Bhalwal",
//         branch_url: "https://site.com",
//         backend_url: "https://portal.site.com",
//       },
//       total_inquiries: 37,
//       total_converted: 12,
//       school_website: 7,
//       facebook: 2,
//       instagram: 0,
//       google_search: 6,
//       whatsapp: 2,
//       referred_parent: 1,
//       walk_in_visit: 2,
//       billboard: 1,
//       newspaper: 10,
//       school_event: 5,
//       other: 1,
//     },
//   ],
//   [
//     {
//       branch_id: 6,
//       branch_resource: {
//         id: 6,
//         name: "Okara",
//         branch_url: "https://site.com",
//         backend_url: "https://portal.site.com",
//       },
//       total_inquiries: 70,
//       total_converted: 32,
//       school_website: 6,
//       facebook: 6,
//       instagram: 8,
//       google_search: 0,
//       whatsapp: 9,
//       referred_parent: 9,
//       walk_in_visit: 10,
//       billboard: 5,
//       newspaper: 3,
//       school_event: 10,
//       other: 4,
//     },
//   ],
// ];

const sourceFields = [
  ["school_website", "School Website"],
  ["facebook", "Facebook"],
  ["instagram", "Instagram"],
  ["google_search", "Google Search"],
  ["whatsapp", "WhatsApp"],
  ["referred_parent", "Referred by a Parent"],
  ["walk_in_visit", "Walk-in Visit"],
  ["billboard", "Billboard / Outdoor Ad"],
  ["newspaper", "Newspaper Ad"],
  ["school_event", "School Event / Open House"],
  ["other", "Other"],
];

const flattenResponseRecords = (response) => {
  const payload = response?.payload ?? response?.data ?? response;
  const records = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
    ? payload.data
    : [];
  return records
    .flat(Infinity)
    .filter((record) => record && !Array.isArray(record));
};

const getSourceName = (record) =>
  record?.source_name ||
  record?.inquiry_source_name ||
  record?.source?.name ||
  record?.inquiry_source?.name ||
  record?.name ||
  `Source ${record?.source_id ?? record?.inquiry_source_id ?? ""}`;

const getSourceId = (record) =>
  record?.source_id ??
  record?.inquiry_source_id ??
  record?.source?.id ??
  record?.inquiry_source?.id ??
  getSourceName(record);

const getCount = (record) =>
  Number(
    record?.total_inquiries ??
      record?.source_count ??
      record?.inquiry_count ??
      record?.total_count ??
      record?.count ??
      record?.total ??
      0,
  );

const normalizeSourceCounts = (response) => {
  const records = flattenResponseRecords(response);
  const values = [];

  const addRecord = (sourceRecord, branchRecord = sourceRecord) => {
    const branchId =
      branchRecord?.branch_id ??
      branchRecord?.campus_id ??
      branchRecord?.branch?.id;
    if (branchId === undefined || branchId === null) return;
    values.push({
      sourceId: String(getSourceId(sourceRecord)),
      sourceName: getSourceName(sourceRecord),
      branchId: String(branchId),
      count: getCount(branchRecord),
    });
  };

  records.forEach((record) => {
    if (
      (record?.branch_id !== undefined || record?.campus_id !== undefined) &&
      sourceFields.some(([field]) => record?.[field] !== undefined)
    ) {
      sourceFields.forEach(([field, label]) =>
        addRecord(
          { source_id: field, source_name: label },
          {
            branch_id: record?.branch_id ?? record?.campus_id,
            count: record?.[field] || 0,
          },
        ),
      );
      return;
    }

    const nested = record?.data || record?.branches || record?.branch_counts;
    if (Array.isArray(nested)) {
      // A month can contain source rows, while a source row can contain branch rows.
      if (record?.month && nested.some((item) => Array.isArray(item?.data))) {
        nested.forEach((source) =>
          (source?.data || source?.branches || []).forEach((branch) =>
            addRecord(source, branch),
          ),
        );
      } else if (
        record?.source_id ||
        record?.inquiry_source_id ||
        record?.source
      ) {
        nested.forEach((branch) => addRecord(record, branch));
      } else if (record?.branch_id || record?.campus_id || record?.branch?.id) {
        nested.forEach((source) =>
          addRecord(source, {
            ...source,
            branch_id:
              record?.branch_id ?? record?.campus_id ?? record?.branch?.id,
          }),
        );
      } else {
        nested.forEach((item) => addRecord(item));
      }
    } else {
      addRecord(record);
    }
  });

  const sources = new Map();
  values.forEach((item) => {
    if (!sources.has(item.sourceId)) {
      sources.set(item.sourceId, {
        id: item.sourceId,
        name: item.sourceName,
        counts: {},
      });
    }
    const source = sources.get(item.sourceId);
    source.counts[item.branchId] =
      (source.counts[item.branchId] || 0) + item.count;
  });
  return Array.from(sources.values());
};

const normalizeBranchSummary = (response) =>
  flattenResponseRecords(response).reduce((summary, record) => {
    const id = record?.branch_id ?? record?.campus_id;
    if (id !== undefined && id !== null) {
      summary[String(id)] = {
        name: record?.branch_resource?.name || `Branch ${id}`,
        totalInquiries: Number(record?.total_inquiries || 0),
        totalConverted: Number(record?.total_converted || 0),
      };
    }
    return summary;
  }, {});

const getMonthName = (value) => {
  if (!value) return "";
  const [year, month] = value.split("-");
  return new Date(Number(year), Number(month) - 1, 1).toLocaleString("en-US", {
    month: "long",
    year: "numeric",
  });
};

const BranchSourceCounts = () => {
  const alert = useAlert();
  const authtoken = localStorage.getItem("userToken");
  const printRef = useRef(null);
  const [exportTable, setExportTable] = useState(null);
  const [branches, setBranches] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [sessionName, setSessionName] = useState("");
  const [month, setMonth] = useState("");
  const [branchId, setBranchId] = useState("");
  const [sourceRows, setSourceRows] = useState([]);
  const [branchSummary, setBranchSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [unitName, setUnitName] = useState("");
  const [logo, setLogo] = useState("");

  const loadLookups = useCallback(async () => {
    try {
      const [
        branchResponse,
        sessionResponse,
        settingsResponse,
      ] = await Promise.all([
        fetch(`${baseUrl}/view-branch-url`, {
          headers: { Authorization: `Bearer ${authtoken}` },
        }),
        fetch(`${baseUrl}/session`, {
          headers: { Authorization: `Bearer ${authtoken}` },
        }),
        fetch(`${baseUrl}/get-settings`, {
          headers: { Authorization: `Bearer ${authtoken}` },
        }),
      ]);
      const [branchData, sessionData, settingsData] = await Promise.all([
        branchResponse.json(),
        sessionResponse.json(),
        settingsResponse.json(),
      ]);
      setBranches(branchData?.payload || []);
      const availableSessions = sessionData?.payload || [];
      setSessions(availableSessions);
      const activeSession = availableSessions.find(
        (session) => String(session.active_academic_year) === "1",
      );
      if (activeSession) {
        setSessionId(String(activeSession.id));
        setSessionName(activeSession.year || activeSession.name || "");
      }
      setUnitName(upperCase(settingsData?.payload?.settings?.unit_name || ""));
      setLogo(settingsData?.payload?.settings?.logo_file || "");
    } catch (error) {
      alert.error("Unable to load report filters");
    }
  }, [alert, authtoken]);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  const getReport = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${baseUrl}/crm-branch-inquiry-source-counts`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${authtoken}`,
          },
          body: JSON.stringify({
            month: month || null,
            branch_id: branchId ? Number(branchId) : null,
            year_id: sessionId ? Number(sessionId) : null,
          }),
        },
      );
      const data = await response.json();
      if (!response.ok || data?.metadata?.success === false) {
        throw new Error(
          data?.metadata?.message || "Unable to load source counts",
        );
      }
      // Use dummyArray for testing. Replace this with `data` when records exist.
      setSourceRows(normalizeSourceCounts(data));
      setBranchSummary(normalizeBranchSummary(data));
    } catch (error) {
      setSourceRows(normalizeSourceCounts([]));
      setBranchSummary(normalizeBranchSummary([]));
      alert.error(
        `${error.message || "Unable to load source counts"}; showing test data`,
      );
    } finally {
      setLoading(false);
    }
  };

  const visibleBranches = useMemo(() => {
    const ids = new Set();
    sourceRows.forEach((source) =>
      Object.keys(source.counts || {}).forEach((id) => ids.add(String(id))),
    );
    const wantedIds = branchId ? [String(branchId)] : Array.from(ids);
    return wantedIds.map((id) => {
      const branch = branches.find((item) => String(item.id) === id);
      return {
        id,
        name:
          branchSummary[id]?.name ||
          branch?.branches ||
          branch?.branch_name ||
          branch?.name ||
          `Branch ${id}`,
      };
    });
  }, [branchId, branches, branchSummary, sourceRows]);

  const branchTotals = useMemo(
    () =>
      visibleBranches.map((branch) =>
        sourceRows.reduce(
          (total, source) => total + Number(source.counts?.[branch.id] || 0),
          0,
        ),
      ),
    [sourceRows, visibleBranches],
  );

  const admissionRatios = useMemo(
    () =>
      visibleBranches.map((branch, index) => {
        const totalInquiries =
          branchSummary[branch.id]?.totalInquiries || branchTotals[index];
        return totalInquiries > 0
          ? Math.round(
              (Number(branchSummary[branch.id]?.totalConverted || 0) /
                totalInquiries) *
                100,
            )
          : 0;
      }),
    [branchSummary, branchTotals, visibleBranches],
  );

  const pieChartOptions = useMemo(
    () => ({
      chart: { type: "pie", toolbar: { show: false } },
      labels: visibleBranches.map((branch) => branch.name),
      title: { text: "Media Source to Admission Ratio", align: "center" },
      legend: { position: "bottom" },
      dataLabels: {
        enabled: true,
        formatter: (percentage, options) =>
          `${options.w.config.series[options.seriesIndex]}%`,
      },
      tooltip: { y: { formatter: (value) => `${value}%` } },
      colors: [
        "#E91E63",
        "#4e6e29",
        "#828c1c",
        "#FFC107",
        "#FF9800",
        "#197c9e",
        "#c90000",
        "#245b17",
        "#f39c12",
        "#6f42c1",
        "#FF5722",
        "#F44336",
      ],
    }),
    [visibleBranches],
  );

  const reportTable = (ref) => (
    <table
      ref={ref}
      className="table table-bordered mb-0"
      style={{ color: "#111" }}
    >
      <thead>
        <tr>
          <th
            className="text-center align-middle"
            style={{
              background: "#343434",
              color: "white",
              border: "1px solid black",
              fontSize: "16px",
            }}
          >
            Media Source
          </th>
          {visibleBranches.map((branch) => (
            <th
              key={branch.id}
              className="text-center align-middle"
              style={{
                background: config?.navBarColor,
                color: "white",
                border: `1px solid ${config?.navBarColor}`,
                fontSize: "16px",
              }}
            >
              {branch.name}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sourceRows.map((source) => (
          <tr key={source.id}>
            <th style={{ border: "1px solid black", fontSize: "14px" }}>
              {source.name}
            </th>
            {visibleBranches.map((branch) => (
              <td
                key={`${source.id}-${branch.id}`}
                className="text-center"
                style={{ border: "1px solid black", fontSize: "14px" }}
              >
                {Number(source.counts?.[branch.id] || 0)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr style={{ background: "#EBEDF3", fontWeight: "bold" }}>
          <th
            className="text-center"
            style={{ border: "1px solid black", fontSize: "14px" }}
          >
            Total
          </th>
          {branchTotals.map((total, index) => (
            <td
              key={visibleBranches[index].id}
              className="text-center"
              style={{ border: "1px solid black", fontSize: "14px" }}
            >
              {total}
            </td>
          ))}
        </tr>
        <tr style={{ fontWeight: "bold" }}>
          <th
            className="text-center"
            style={{
              color: config?.txtColor,
              border: "1px solid black",
              fontSize: "14px",
            }}
          >
            Source to Admission Ratio
          </th>
          {admissionRatios.map((ratio, index) => (
            <td
              key={`ratio-${visibleBranches[index].id}`}
              className="text-center"
              style={{ border: "1px solid black", fontSize: "14px" }}
            >
              {ratio}%
            </td>
          ))}
        </tr>
      </tfoot>
    </table>
  );

  const pageStyle = `
    @page { size: A4 landscape !important; }
  `;

  const branchName = branchId
    ? branchSummary[String(branchId)]?.name ||
      branches.find((branch) => String(branch.id) === String(branchId))
        ?.branches ||
      branches.find((branch) => String(branch.id) === String(branchId))?.name
    : "All Branches";

  return (
    <Paper style={{ marginTop: 10, marginBottom: 10, padding: 15 }}>
      <div className="row align-items-end">
        <div className="col-md-3 mb-3">
          <label>
            <b>Select Session</b>
          </label>
          <select
            className="form-control form-select"
            value={sessionId}
            onChange={(event) => {
              const value = event.target.value;
              setSessionId(value);
              const selected = sessions.find(
                (session) => String(session.id) === value,
              );
              setSessionName(selected?.year || selected?.name || "");
            }}
          >
            <option value="">All Sessions</option>
            {sessions.map((session) => (
              <option key={session.id} value={session.id}>
                {session.year || session.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-3 mb-3">
          <label>
            <b>Select Month</b>
          </label>
          <input
            type="month"
            className="form-control"
            value={month}
            onChange={(event) => setMonth(event.target.value)}
          />
        </div>
        <div className="col-md-3 mb-3">
          <label>
            <b>Select Branch</b>
          </label>
          <select
            className="form-control form-select"
            value={branchId}
            onChange={(event) => setBranchId(event.target.value)}
          >
            <option value="">All Branches</option>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.branches || branch.branch_name || branch.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-3 mb-3 d-flex">
          <button
            className="btn btn-primary mr-2"
            onClick={getReport}
            disabled={loading}
          >
            {loading ? "Loading..." : "Show"}
          </button>
          {sourceRows.length > 0 && (
            <>
              <ReactToPrint
                documentTitle="Branch Source Counts"
                pageStyle={pageStyle}
                onBeforeGetContent={() => setPrintLoading(true)}
                onAfterPrint={() => setPrintLoading(false)}
                onPrintError={() => setPrintLoading(false)}
                trigger={() => (
                  <button
                    className="btn btn-primary mr-2"
                    disabled={printLoading}
                  >
                    {printLoading ? "Printing..." : "Print"}
                  </button>
                )}
                content={() => printRef.current}
              />
              <DownloadTableExcel
                filename="Branch Source Counts"
                sheet="Media Impact"
                currentTableRef={exportTable}
              >
                <button
                  className="btn btn-primary"
                  disabled={exportLoading || !exportTable}
                  onClick={() => {
                    setExportLoading(true);
                    window.setTimeout(() => setExportLoading(false), 1500);
                  }}
                >
                  {exportLoading ? "Exporting..." : "Export"}
                </button>
              </DownloadTableExcel>
            </>
          )}
        </div>
      </div>

      {loading && (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      )}
      {!loading && sourceRows.length > 0 && visibleBranches.length > 0 && (
        <div className="branch-source-report mt-4 p-3">
          <div className="d-flex align-items-center mb-3">
            {logo && (
              <img
                src={logo}
                alt="School logo"
                style={{ width: 90, height: 90, objectFit: "contain" }}
              />
            )}
            <div className="ml-3">
              <h2>{unitName}</h2>
              <h3>
                Branch Source Counts Report
                {month ? ` for ${getMonthName(month)}` : ""}
                {sessionName ? ` (Session ${sessionName})` : ""}
              </h3>
              <h5>Branch: {branchName}</h5>
            </div>
          </div>
          <div className="table-responsive">{reportTable(setExportTable)}</div>
          <div className="mt-8" style={{ maxWidth: 700, margin: "0 auto" }}>
            <ReactApexChart
              options={pieChartOptions}
              series={admissionRatios}
              type="pie"
              height={390}
            />
          </div>
        </div>
      )}

      <div
        ref={printRef}
        id="printSection"
        className="branch-source-report"
        style={{
          visibility: "hidden",
        }}
      >
        <div className="d-flex align-items-center mb-3">
          {logo && (
            <img
              src={logo}
              alt="School logo"
              style={{ width: 90, height: 90, objectFit: "contain" }}
            />
          )}
          <div className="ml-3">
            <h2>{unitName}</h2>
            <h3>
              Branch Source Counts Report
              {month ? ` for ${getMonthName(month)}` : ""}
              {sessionName ? ` (Session ${sessionName})` : ""}
            </h3>
            <h5>Branch: {branchName}</h5>
          </div>
        </div>
        <table className="mb-0" style={{ color: "#111", width: "100%" }}>
          <thead>
            <tr>
              <th
                className="text-center align-middle"
                style={{
                  border: "1px solid black",
                  fontSize: "16px",
                  padding: "10px",
                  whiteSpace: "normal",
                }}
              >
                Media Source
              </th>
              {visibleBranches.map((branch) => (
                <th
                  key={branch.id}
                  className="text-center align-middle"
                  style={{
                    border: "1px solid black",
                    fontSize: "16px",
                    padding: "10px",
                    whiteSpace: "normal",
                  }}
                >
                  {branch.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sourceRows.map((source) => (
              <tr key={source.id}>
                <td
                  className="text-center "
                  style={{
                    border: "1px solid black",
                    fontSize: "14px",
                    padding: "1px",
                    whiteSpace: "normal",
                  }}
                >
                  {source.name}
                </td>
                {visibleBranches.map((branch) => (
                  <td
                    key={`${source.id}-${branch.id}`}
                    className="text-center"
                    style={{
                      border: "1px solid black",
                      fontSize: "14px",
                      padding: "1px",
                      whiteSpace: "normal",
                    }}
                  >
                    {Number(source.counts?.[branch.id] || 0)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr
              style={{
                fontWeight: "bold",
                fontSize: "16px",
                padding: "1px",
                whiteSpace: "normal",
              }}
            >
              <th className="text-center" style={{ border: "1px solid black" }}>
                Total
              </th>
              {branchTotals.map((total, index) => (
                <td
                  key={visibleBranches[index].id}
                  className="text-center"
                  style={{
                    border: "1px solid black",
                    fontSize: "16px",
                    padding: "1px",
                    whiteSpace: "normal",
                  }}
                >
                  {total}
                </td>
              ))}
            </tr>
            <tr style={{ fontWeight: "bold" }}>
              <th
                className="text-center"
                style={{
                  border: "1px solid black",
                  fontSize: "16px",
                  padding: "1px",
                  whiteSpace: "normal",
                }}
              >
                Source to Admission Ratio
              </th>
              {admissionRatios.map((ratio, index) => (
                <td
                  key={`ratio-${visibleBranches[index].id}`}
                  className="text-center"
                  style={{
                    border: "1px solid black",
                    fontSize: "16px",
                    padding: "1px",
                    whiteSpace: "normal",
                  }}
                >
                  {ratio}%
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </Paper>
  );
};

export default BranchSourceCounts;
